import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import {
  CancelCircleIcon,
  CheckmarkCircle01Icon,
  Clock01Icon,
  PauseCircleIcon,
  PlayCircleIcon,
  StopCircleIcon,
  UnavailableIcon,
  type TerraIcon,
} from '@terra/ui-icons';
import { token, type Token } from '@terra/ui-styled-system';
import type { ReactNode } from 'react';

type TraceStatus = TracePreviewDto['status'];

export const TRACE_STATUS_COLOR_TOKENS = {
  RUNNING: 'colors.fg.default',
  PAUSED: 'colors.fg.status.duplicate',
  COMPLETED: 'colors.fg.moss.default',
  FAILED: 'colors.fg.severity.critical',
  ABORTED: 'colors.fg.status.duplicate',
  BLOCKED: 'colors.fg.severity.high',
} as const satisfies Record<TraceStatus, Extract<Token, `colors.${string}`>>;

const TRACE_STATUS_ICONS: Record<TraceStatus, TerraIcon> = {
  RUNNING: PlayCircleIcon,
  PAUSED: PauseCircleIcon,
  COMPLETED: CheckmarkCircle01Icon,
  FAILED: CancelCircleIcon,
  ABORTED: StopCircleIcon,
  BLOCKED: UnavailableIcon,
};

export function getStatusColor(status: string) {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'RUNNING':
      return 'terra';
    case 'STOPPED':
      return 'orange';
    case 'ABORTED':
      return 'gray';
    case 'BLOCKED':
      return 'purple';
    default:
      return 'gray';
  }
}

function isTraceStatus(status: string): status is TraceStatus {
  return Object.hasOwn(TRACE_STATUS_ICONS, status);
}

export function getStatusIcon(status: string, size = 14): ReactNode {
  if (!isTraceStatus(status)) {
    return <Clock01Icon size={size} color={token('colors.fg.default')} />;
  }

  const Icon = TRACE_STATUS_ICONS[status];

  return <Icon size={size} color={token(TRACE_STATUS_COLOR_TOKENS[status])} />;
}
